import React, { useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, IconButton, Tooltip } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import moment from 'moment';
import { StandardProductForm, SeatedProductForm } from './StocksProductForm';
import { normalizeEventType } from '../../utils/eventFormUtils';
import { generateTicketID } from '../../utils/formatting';

const formatStockDateTimeLabel = (stock) => {
  const date = stock?.date ? moment(stock.date, 'YYYY-MM-DD', true) : null;
  const datePart = date?.isValid() ? date.format('DD/MM/YYYY') : '-';
  return stock?.time ? `${datePart} - ${stock.time}` : datePart;
};

const emptyProduct = () => ({
  id: generateTicketID(),
  status: 0,
  refundable: false,
  hasDownPayment: false,
  hasBasketLimit: false,
  sold: 0,
  price: 0,
  stock: 0,
});

/**
 * Eski SimpleFormIterator görünümü (+ / − ikonları),
 * ekleme setValue ile — nested `_stockRecord` altında ArrayInput Add bozuktu.
 */
const ProductGroupEditor = ({
  title,
  source,
  note,
  sourceLang,
  isEditForm = true,
  allowRemove = true,
  allowAdd = true,
  ProductFields = StandardProductForm,
}) => {
  const { getValues, setValue } = useFormContext();
  const watched = useWatch({ name: source });
  const items = Array.isArray(watched) ? watched : [];

  const handleAdd = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      const current = getValues(source);
      const list = Array.isArray(current) ? [...current] : [];
      list.push(emptyProduct());
      setValue(source, list, { shouldDirty: true, shouldTouch: true });
    },
    [getValues, setValue, source],
  );

  const handleRemove = useCallback(
    (index) => (event) => {
      event.preventDefault();
      event.stopPropagation();
      const current = getValues(source);
      const list = Array.isArray(current) ? [...current] : [];
      list.splice(index, 1);
      setValue(source, list, { shouldDirty: true, shouldTouch: true });
    },
    [getValues, setValue, source],
  );

  return (
    <div style={{ border: '1px dashed #000', padding: 5 }}>
      <h3>
        {title}{' '}
        <span style={{ color: 'orange', fontStyle: 'italic', fontSize: 12 }}>
          (* all fields required)
        </span>
      </h3>
      {note && (
        <span
          style={{
            backgroundColor: '#0ABAB5',
            color: 'white',
            borderRadius: 10,
            fontSize: 12.5,
            padding: 5,
            marginTop: -10,
            marginBottom: 8,
            display: 'inline-block',
          }}
        >
          {note}
        </span>
      )}

      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {items.map((item, index) => {
          const getSource = (field) => `${source}.${index}.${field}`;
          return (
            <li
              key={item?.id || `${source}-${index}`}
              style={{
                display: 'flex',
                listStyleType: 'none',
                borderBottom: '1px solid #e0e0e0',
                paddingBottom: 8,
                marginBottom: 8,
              }}
            >
              <section style={{ flex: 1, minWidth: 0 }}>
                <ProductFields
                  getSource={getSource}
                  showSold={false}
                  scopedFormData={item || {}}
                  isEditForm={isEditForm}
                  sourceLang={sourceLang}
                />
              </section>
              {allowRemove && (
                <span style={{ marginTop: 4, marginLeft: 4 }}>
                  <Tooltip title="Sil">
                    <IconButton
                      type="button"
                      size="small"
                      color="primary"
                      onClick={handleRemove(index)}
                      aria-label="remove"
                    >
                      <RemoveCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {allowAdd && (
        <div>
          <Tooltip title="Ekle">
            <IconButton
              type="button"
              size="small"
              color="primary"
              onClick={handleAdd}
              aria-label="add"
              className={`button-add button-add-${source}`}
            >
              <AddCircleOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export const StandardProductFormComponent = ({ stock, formPrefix = '', sourceLang = 'tr' }) => {
  const stockType = normalizeEventType(stock?.type);
  const locked = stockType === 3;

  if (locked) {
    return (
      <p style={{ color: '#6b7280' }}>
        Seated stock products are managed via layout seat assignments.
      </p>
    );
  }

  return (
    <React.Fragment>
      <h2>Details</h2>
      <div style={{ fontSize: 14 }}>
        <div>
          <span>
            <b>Stock ID:&nbsp;</b>
          </span>
          <span>{stock?.id ?? '-'}</span>
        </div>
        <div>
          <span>
            <b>Date / Time:&nbsp;</b>
          </span>
          <span>{formatStockDateTimeLabel(stock)}</span>
        </div>
      </div>

      <h2>Products / Stocks</h2>
      <Box display={{ xs: 'block', sm: 'flex', width: '100%', marginBottom: 20 }}>
        <Box flex={1} mr={{ xs: 0, sm: '0.5em' }}>
          <ProductGroupEditor
            title="Special Deal"
            source={`${formPrefix}stock.deal`}
            sourceLang={sourceLang}
            isEditForm
          />
        </Box>
        <Box flex={1} ml={{ xs: 0, sm: '0.5em' }}>
          <ProductGroupEditor
            title="Reservation"
            source={`${formPrefix}stock.guestlist`}
            sourceLang={sourceLang}
            isEditForm={false}
          />
        </Box>
      </Box>

      <Box display={{ xs: 'block', sm: 'flex', width: '100%', marginBottom: 20 }}>
        <Box flex={1} mr={{ xs: 0, sm: '0.5em' }}>
          <ProductGroupEditor
            title="General"
            source={`${formPrefix}stock.general`}
            sourceLang={sourceLang}
            isEditForm
          />
        </Box>
        <Box flex={1} ml={{ xs: 0, sm: '0.5em' }}>
          <ProductGroupEditor
            title="VIP List"
            source={`${formPrefix}stock.vip`}
            sourceLang={sourceLang}
            isEditForm
          />
        </Box>
      </Box>

      <Box display={{ xs: 'block', sm: 'flex', width: '100%' }}>
        <Box flex={1} mr={{ xs: 0, sm: '0.5em' }}>
          <ProductGroupEditor
            title="Extras"
            source={`${formPrefix}stock.extras`}
            sourceLang={sourceLang}
            isEditForm
          />
        </Box>
        <Box flex={1} ml={{ xs: 0, sm: '0.5em' }}>
          <ProductGroupEditor
            title="Invitations"
            source={`${formPrefix}stock.invitation`}
            note="Can create invitations for guests to attend the event."
            sourceLang={sourceLang}
            isEditForm
          />
        </Box>
      </Box>
    </React.Fragment>
  );
};

export const SeatedProductFormComponent = ({ stock, formPrefix = '', sourceLang = 'tr' }) => {
  const stockType = normalizeEventType(stock?.type);
  if (stockType === 3) {
    return (
      <p style={{ color: '#6b7280' }}>
        Seated stock products are managed via layout seat assignments.
      </p>
    );
  }

  return (
    <ProductGroupEditor
      title="Tickets"
      source={`${formPrefix}stock`}
      sourceLang={sourceLang}
      isEditForm
      allowRemove={false}
      ProductFields={SeatedProductForm}
    />
  );
};
